import Vue from 'vue';
import * as Sentry from '@sentry/browser';

/**
 * 현재 발생하는 에러들을 Sentry에서 제외하기 위한 필터링 함수
 */
function shouldExcludeError(errorMessage) {
  const excludePatterns = [
    // Store 관련 에러
    '보고서에 연결된 사이트 정보를 찾을 수 없습니다',
    
    // ThermalDataHolder 관련 에러
    'Thermal data is insufficient',
    '데이터가 충분하지 않음',
    'THERMAL DATA HOLDER',
    
    // undefined 관련 에러
    'report_seq:undefined',
    'reportId undefined',
    'viewparams=report_seq:undefined',
    
    // HTTP 관련 에러
    'Bad Request',
    '400 (Bad Request)',
    
    // WFS 요청 관련 에러
    'map.ssolvit.com/wfs',
    'module_defects_by_new_type',
    
    // OpenLayers 관련 에러
    'trycatch.js',
    'featureloader.js'
  ];
  
  return excludePatterns.some(pattern => errorMessage.includes(pattern));
}

/**
 * Sentry 초기화 함수
 * 환경에 따라 다른 DSN과 설정을 적용
 * 
 * 📊 월 5,000건 한도 관리 전략:
 * - sampleRate: 전체 에러의 10%만 샘플링
 * - ignoreErrors: 무의미한 에러 패턴 무시
 * - denyUrls: 외부 스크립트/확장 프로그램 에러 제외
 * - beforeSend: 추가 필터링 로직
 */
export function initSentry() {
  // 개발 환경에서는 Sentry 비활성화
  // 로컬 테스트 시: VUE_APP_SENTRY_FORCE_ENABLE=true 설정
  const forceEnable = process.env.VUE_APP_SENTRY_FORCE_ENABLE === 'true';
  
  if (process.env.NODE_ENV === 'development' && !forceEnable) {
    console.log('🔧 개발 환경: Sentry 비활성화 (활성화: VUE_APP_SENTRY_FORCE_ENABLE=true)');
    return;
  }

  // DSN 설정 (환경 변수 또는 하드코딩)
  const sentryDsn = process.env.VUE_APP_SENTRY_DSN || 'https://330d109e2785fc4c0b2417b9c2bca840@o4510751661752320.ingest.us.sentry.io/4510751758417920';
  
  if (!sentryDsn) {
    console.warn('⚠️ SENTRY_DSN이 설정되지 않아 Sentry를 초기화하지 않습니다.');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    
    // 환경 설정
    environment: process.env.NODE_ENV || 'production',
    
    // 릴리즈 버전 (package.json에서 가져오기)
    release: process.env.VUE_APP_VERSION || 'unknown',

    // =====================================================
    // 📊 월 5,000건 한도 관리 설정
    // =====================================================
    
    /**
     * 샘플링 비율 (0.0 ~ 1.0)
     * 0.1 = 전체 에러의 10%만 Sentry로 전송
     * 
     * 계산 예시:
     * - 일일 에러 500건 발생 시 → 50건만 전송 → 월 약 1,500건
     * - 일일 에러 1000건 발생 시 → 100건만 전송 → 월 약 3,000건
     * 
     * 필요에 따라 조정: 0.05(5%), 0.2(20%) 등
     */
    sampleRate: 0.1,

    /**
     * 무시할 에러 패턴 (정규식 또는 문자열)
     * 자주 발생하지만 실제로 중요하지 않은 에러들
     */
    ignoreErrors: [
      // 네트워크 관련 에러
      'Network Error',
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'NetworkError when attempting to fetch resource',
      'The network connection was lost',
      'net::ERR_',
      'TypeError: Failed to fetch',
      'TypeError: NetworkError',
      'TypeError: Load failed',
      
      // 청크 로딩 에러 (코드 분할 관련)
      'ChunkLoadError',
      'Loading chunk',
      'Loading CSS chunk',
      
      // 브라우저 확장 프로그램 관련
      'ResizeObserver loop',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      
      // 사용자 중단 에러
      'AbortError',
      'The operation was aborted',
      'The user aborted a request',
      
      // 권한/인증 관련 (의도된 리다이렉트인 경우)
      'Request failed with status code 401',
      'Request failed with status code 403',
      
      // 스크립트 로딩 에러 (광고/추적 스크립트 등)
      'Script error.',
      'Script error',
      
      // 타사 라이브러리 에러
      'Non-Error promise rejection',
      'Non-Error exception captured',
      
      // Vuetify/Vue 관련 경미한 에러
      'Cannot read property',
      'Cannot read properties of undefined',
      'Cannot read properties of null',
      
      // 메모리 관련
      'out of memory',
      'Out of memory',
      
      // 현재 발생하는 애플리케이션 에러들 (Sentry 제외)
      '보고서에 연결된 사이트 정보를 찾을 수 없습니다',
      'Thermal data is insufficient',
      '데이터가 충분하지 않음',
      'THERMAL DATA HOLDER',
      
      // reportId undefined 관련 에러들
      'report_seq:undefined',
      'viewparams=report_seq:undefined',
      
      // Promise rejection 관련
      'Uncaught (in promise)',
      'Uncaught promise',
      'Unhandled promise rejection',
    ],

    /**
     * 에러를 무시할 URL 패턴
     * 외부 스크립트나 브라우저 확장에서 발생하는 에러 제외
     */
    denyUrls: [
      // 브라우저 확장 프로그램
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      
      // 광고/분석 스크립트
      /google.*analytics/i,
      /googletagmanager/i,
      /facebook\.net/i,
      /connect\.facebook/i,
      /doubleclick\.net/i,
      
      // 기타 외부 스크립트
      /cdn\.jsdelivr/i,
      /unpkg\.com/i,
      
      // 현재 발생하는 WFS 요청 에러 URL 패턴 제외
      /map\.ssolvit\.com\/wfs/i,
      /report_seq:undefined/i,
    ],
    
    // 에러 필터링 (중요한 애플리케이션 에러만 전송)
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const errorMessage = error?.message || event?.message || '';
      
      // 파일명 기반 필터링 (특정 파일에서 발생하는 모든 에러 제외)
      const stackTrace = error?.stack || event?.exception?.values?.[0]?.stacktrace || '';
      const excludeFiles = [
        'ThermalDataHolder.js',
        'explorer.store.js',
        'trycatch.js'  // OpenLayers trycatch.js 에러 제외
      ];
      
      for (const fileName of excludeFiles) {
        if (stackTrace.includes(fileName)) {
          return null; // 특정 파일에서 발생한 에러는 Sentry로 전송하지 않음
        }
      }
      
      // 1. ChunkLoadError 제외 (웹팩 동적 import 실패)
      if (error?.name === 'ChunkLoadError') {
        return null;
      }
      
      // 2. Axios/HTTP 에러 중 일부 제외
      if (error?.response) {
        const status = error.response.status;
        // 400, 401, 403, 404는 일반적인 클라이언트 에러이므로 제외
        if ([400, 401, 403, 404].includes(status)) {
          return null;
        }
      }
      
      // 3. 네트워크 에러 제외 (오프라인/불안정한 연결)
      if (errorMessage.includes('Network Error') || 
          errorMessage.includes('timeout') ||
          error?.code === 'ECONNABORTED') {
        return null;
      }
      
      // 4. 현재 발생하는 특정 애플리케이션 에러들 제외
      if (shouldExcludeError(errorMessage)) {
        return null;
      }
      
      // 5. 빈 에러 메시지 제외
      if (!errorMessage) {
        return null;
      }
      
      return event;
    },

    // 사용자 정보 자동 수집 비활성화 (개인정보 보호)
    sendDefaultPii: false,
    
    // Debug 모드 (문제 해결 시에만 true로 설정)
    debug: forceEnable, // 로컬 테스트 시에만 디버그 모드 활성화
    
    // 로컬 변수 캡처 (에러 컨텍스트 향상)
    attachStacktrace: true,
    
    // 브레드크럼 수 제한 (메모리/용량 절약)
    maxBreadcrumbs: 30,
  });

  // Vue 2 전용 에러 핸들러 설정 (필터링 포함)
  Vue.config.errorHandler = (error, vm, info) => {
    console.error('Vue 에러:', error, info);
    
    // 현재 발생하는 에러들은 Sentry로 전송하지 않음
    if (shouldExcludeError(error?.message || '')) {
      return;
    }
    
    // 제외되지 않은 에러만 Sentry로 전송
    Sentry.captureException(error, {
      contexts: {
        vue: {
          component_name: vm?.$options.name || 'Unknown',
          props: vm?.$props || {},
          route: vm?.$route?.path || 'Unknown'
        }
      },
      extra: {
        vue_info: info
      }
    });
  };

  // Unhandled Promise Rejection 핸들러 설정 (필터링 포함)
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      const error = event?.reason;
      const errorMessage = error?.message || event?.reason || '';
      
      // 현재 발생하는 Promise rejection 에러들 제외
      if (shouldExcludeError(errorMessage)) {
        event.preventDefault(); // 브라우저 콘솔 에러 표시는 유지하되 Sentry 전송 방지
        return;
      }
      
      // 제외되지 않은 Promise rejection만 Sentry로 전송
      console.error('Unhandled Promise Rejection:', error);
      Sentry.captureException(error, {
        extra: {
          type: 'unhandledrejection',
          reason: event.reason
        }
      });
    });
  }

  console.info('✅ Sentry 초기화 완료');
  
  // 브라우저 콘솔에서 Sentry에 접근 가능하도록 전역 설정
  if (typeof window !== 'undefined') {
    window.Sentry = Sentry;
  }
}

/**
 * 수동으로 에러 캐치하여 Sentry로 전송 (필터링 포함)
 */
export function captureError(error, context = {}) {
  if (process.env.NODE_ENV === 'development') {
    console.error('🐛 개발 환경 에러:', error, context);
    return;
  }
  
  // 현재 발생하는 에러들은 Sentry로 전송하지 않음
  if (shouldExcludeError(error?.message || '')) {
    return;
  }
  
  Sentry.captureException(error, {
    extra: context
  });
}

/**
 * 커스텀 메시지를 Sentry로 전송
 */
export function captureMessage(message, level = 'info', extra = {}) {
  if (process.env.NODE_ENV === 'development') {
    console.log(`🔍 개발 환경 메시지 [${level}]:`, message, extra);
    return;
  }
  
  Sentry.captureMessage(message, level, {
    extra
  });
}

/**
 * 사용자 컨텍스트 설정
 */
export function setUserContext(user) {
  Sentry.configureScope((scope) => {
    scope.setUser(user);
  });
}

export default {
  initSentry,
  captureError,
  captureMessage,
  setUserContext
};